import React, {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
  ReactNode,
  AriaAttributes,
  useContext,
} from "react";
import {
  Button,
  CloseIconThin,
  HelpDrawer,
  HelpDrawerToggle,
} from "@cmsgov/ds-medicare-gov";
import { makeValidQuerySelector } from "../helpers";
import classNames from "classnames";
import StickyFooter from "./StickyFooter";
import uniqueId from "lodash.uniqueid";
import { AppContext } from "../app/store";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../app/contexts/Analytics/types";

interface MctHelpDrawerToggleProps {
  open: boolean;
  anyDrawerOpen: boolean;
  openDrawer: () => void;
  toggleClassName?: string;
  inlineToggle?: boolean;
  toggleText: ReactNode;
}

const internalToggleClassName = "help-drawer-toggle";

export const MctHelpDrawerToggle: FunctionComponent<
  MctHelpDrawerToggleProps
> = ({
  open,
  anyDrawerOpen,
  openDrawer,
  toggleClassName,
  inlineToggle,
  toggleText,
  ...props
}) => {
  return (
    <HelpDrawerToggle
      helpDrawerOpen={anyDrawerOpen || open}
      showDrawer={openDrawer}
      className={`${internalToggleClassName} ${toggleClassName}`}
      inline={inlineToggle}
      aria-expanded={open}
      {...props}
    >
      {toggleText}
    </HelpDrawerToggle>
  );
};

type ModifiedHelpDrawerProps = Omit<
  React.ComponentProps<typeof HelpDrawer>,
  "onCloseClick" | "heading"
>;

interface ButtonInterface
  extends Omit<
      React.ComponentPropsWithoutRef<typeof Button>,
      "render" | "children"
    >,
    AriaAttributes {
  id?: string;
  shouldCloseDrawer?: boolean;
  text: string;
}

export interface MctHelpDrawerFooterProps {
  className?: string;
  id?: string;
  children?: React.ReactNode;
  buttons: ButtonInterface[];
}

interface MctHelpDrawerProps extends ModifiedHelpDrawerProps {
  large?: boolean;
  drawerTitle: string;
  closeDrawer: () => void;
  footer?: MctHelpDrawerFooterProps;
  id?: string;
  className?: string;
  dialogClassName?: string;
}

export const MctHelpDrawer: FunctionComponent<MctHelpDrawerProps> = ({
  large,
  drawerTitle,
  closeDrawer,
  footer,
  children,
  id,
  className,
  dialogClassName,
  ...props
}) => {
  const { dispatch } = useContext(AppContext);
  const classes = classNames(
    "mct-c-help-drawer ds-u-text-align--left mct-u-position--absolute",
    className,
    large ? "mct-c-help-drawer__large" : ""
  );

  return (
    <div className={classes} id={id} role="complementary">
      <HelpDrawer
        closeButtonText={<CloseIconThin />}
        heading={drawerTitle}
        onCloseClick={closeDrawer}
        className={dialogClassName}
        {...props}
      >
        {children}
        {footer && (
          <StickyFooter className={footer.className} id={footer.id}>
            {footer?.children}
            {footer.buttons.map(button => {
              const { text, onClick, shouldCloseDrawer, ...props } = button;
              return (
                <Button
                  key={text}
                  onClick={() => {
                    onClick && onClick();
                    if (shouldCloseDrawer) {
                      closeDrawer();
                    }
                    dispatch({
                      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                      settings: {
                        button: {
                          buttonStyle: AnalyticsButtonStyle.DEFAULT,
                          buttonType: AnalyticsButtonType.BUTTON,
                          text,
                        },
                      },
                    });
                  }}
                  {...props}
                >
                  {text}
                </Button>
              );
            })}
          </StickyFooter>
        )}
      </HelpDrawer>
    </div>
  );
};

export interface HelpDrawerAnalytics {
  openDrawer: () => void;
  closeDrawer: () => void;
}

export interface HelpDrawerWrapperProps extends ModifiedHelpDrawerProps {
  inlineToggle?: boolean;
  toggleText: ReactNode;
  toggleClassName?: string;
  drawerTitle: string;
  large?: boolean;
  tealiumProps?: {
    callingLabel: string;
    label: string;
    eventCategory?: string;
  };
  footer?: MctHelpDrawerFooterProps;
  mctAnalytics?: HelpDrawerAnalytics;
  onOpenDrawer?: () => void;
  onCloseDrawer?: () => void;
  className?: string;
  dialogClassName?: string;
}

const HelpDrawerWrapper: FunctionComponent<HelpDrawerWrapperProps> = ({
  inlineToggle = false,
  toggleText,
  toggleClassName = "",
  drawerTitle,
  tealiumProps,
  children,
  large,
  footer,
  mctAnalytics,
  onOpenDrawer = () => {},
  onCloseDrawer = () => {},
  className,
  dialogClassName,
  ...props
}) => {
  const { dispatch } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  // Use this state value to prevent focus going to any toggle when a drawer
  // is closed because the user has opened another. If all drawers are closed
  // focus can go to the last used toggle (see https://design.cms.gov/components/help-drawer/)
  // `HelpDrawerToggle` -> `helpDrawerOpen` prop
  const [anyDrawerOpen, setAnyDrawerOpen] = useState(false);
  // We need a reference to the open state variable so that the clickOutsideHandler
  // function can be updated appropriately, otherwise it just uses the initial value
  const openRef = useRef(open);
  const [controlName, setControlName] = useState("");
  useEffect(() => {
    setControlName(
      `control-${makeValidQuerySelector(drawerTitle)}-${uniqueId()}`
    );
  }, []);

  const openDrawer = (): void => {
    if (tealiumProps) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: tealiumProps.callingLabel,
          event_label: tealiumProps.label,
          other_props: tealiumProps.eventCategory
            ? { event_category: tealiumProps.eventCategory }
            : undefined,
        },
      });
    }
    if (mctAnalytics) {
      mctAnalytics.openDrawer();
    }
    onOpenDrawer();
    setOpen(true);
  };

  const closeDrawer = (): void => {
    if (tealiumProps) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: `${tealiumProps.callingLabel} - ${tealiumProps.label}`,
          event_label: "Close",
          other_props: tealiumProps.eventCategory
            ? { event_category: tealiumProps.eventCategory }
            : undefined,
        },
      });
    }
    if (mctAnalytics) {
      mctAnalytics.closeDrawer();
    }
    onCloseDrawer();
    setOpen(false);
  };

  /**
   * A close handler for drawer click-outside events, only. The `HelpDrawer`
   * component has its own prop for the close button handler (`closeDrawer`).
   * Checks that the target isn't inside the drawer or the toggle
   * that opens the drawer, and that the drawer is currently open
   */
  const clickOutsideHandler = (e: MouseEvent): void => {
    if (!openRef.current) {
      return;
    }
    const target = e.target as HTMLElement;
    const buttonTarget = target.closest("button");
    const isInsideDrawer = target.closest(`#${controlName}`);
    const isThisDrawerToggle =
      target.matches(`[aria-controls="${controlName}"]`) ||
      buttonTarget?.matches(`[aria-controls="${controlName}"]`);
    if (!isInsideDrawer && !isThisDrawerToggle) {
      closeDrawer();
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("click", clickOutsideHandler);
      setAnyDrawerOpen(true);
    } else {
      document.removeEventListener("click", clickOutsideHandler);
      const otherOpenDrawer = Boolean(
        document.querySelector(
          `.${internalToggleClassName}[aria-expanded="true"]`
        )
      );
      setAnyDrawerOpen(otherOpenDrawer);
    }

    // Maintain the state of the boolean ref
    openRef.current = open;
    return () => {
      document.removeEventListener("click", clickOutsideHandler);
    };
  }, [open]);

  const optionalDrawerProps: Pick<ModifiedHelpDrawerProps, "headingLevel"> = {};
  if (props.headingLevel) {
    optionalDrawerProps.headingLevel = props.headingLevel;
  }

  return (
    <>
      <MctHelpDrawerToggle
        open={open}
        anyDrawerOpen={anyDrawerOpen}
        openDrawer={openDrawer}
        toggleClassName={toggleClassName}
        inlineToggle={inlineToggle}
        toggleText={toggleText}
        aria-controls={controlName}
      />
      {open && (
        <MctHelpDrawer
          closeDrawer={closeDrawer}
          large={large}
          drawerTitle={drawerTitle}
          footer={footer}
          id={controlName}
          className={className}
          dialogClassName={dialogClassName}
          {...optionalDrawerProps}
        >
          {children}
        </MctHelpDrawer>
      )}
    </>
  );
};

export default HelpDrawerWrapper;
